<template>
  <AppLayout>
    <div class="flex flex-col items-center gap-4">
      <h1>Connected Apps</h1>
      <p class="m-0 text-sm text-surface-400 max-w-xl text-center">
        MCP clients authorized to access Phottr with your account. Revoking a client signs it
        out; it can reconnect by going through the authorization flow again.
      </p>

      <DataTable :value="sessions" :loading="loading" tableStyle="min-width: 40rem">
        <Column field="clientName" header="Client" />
        <Column header="Connected">
          <template #body="{ data }">
            {{ formatDate(data.created) }}
          </template>
        </Column>
        <Column header="Last used">
          <template #body="{ data }">
            {{ formatDate(data.lastUsed) }}
          </template>
        </Column>
        <Column header="Expires">
          <template #body="{ data }">
            {{ formatDate(data.expires) }}
          </template>
        </Column>
        <Column>
          <template #body="{ data }">
            <Button
              label="Revoke"
              severity="danger"
              size="small"
              outlined
              @click="revoke(data.id)"
            />
          </template>
        </Column>
        <template #empty>No connected apps.</template>
      </DataTable>
    </div>
  </AppLayout>
</template>

<script setup lang="ts">
import { ref } from 'vue';
import dayjs from 'dayjs';
import type { OauthSessionDto } from '@/api/generated';
import AppLayout from '@/components/AppLayout.vue';
import { useApiClient } from '@/api/apiClient';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import Button from 'primevue/button';

const { api } = useApiClient();

const sessions = ref<OauthSessionDto[]>([]);
const loading = ref(false);

function formatDate(epochMs: number): string {
  return dayjs(epochMs).format('MMM D, YYYY HH:mm');
}

async function loadSessions() {
  loading.value = true;
  try {
    const { data, error } = await api.oauthControllerListSessions();
    if (error) {
      throw new Error('Failed to load connected apps');
    }
    sessions.value = data ?? [];
  } finally {
    loading.value = false;
  }
}

async function revoke(id: string) {
  const { error } = await api.oauthControllerRevokeSession({ path: { id } });
  if (error) {
    throw new Error('Failed to revoke session');
  }
  await loadSessions();
}

loadSessions();
</script>
